import { action, computed, IReactionDisposer, observable, ObservableMap } from 'mobx'
import { Subscription } from 'rxjs'
import { appsyncApi } from '../../../appsync/graphql/api'
import { AppSyncEventService } from './AppSyncEventService'
import {
  IAppSyncCallbacks,
  ISubscriptionCallbacks,
} from '../../../appsync/graphql/interfaces/subscription'
import { IEventService } from './interfaces'
import {
  BlazepayAPI,
  BlazepayConfigModels,
  BlazepayEnums,
  BlazepayModels,
  BlazepayPaymentModels,
  BlazepayPaymentOptionModels,
  BlazepayTerminalModels,
} from '@getgreenline/payments'
import { captureError, sentryWrapper } from '../../../utilities/logging'
import { parseErrorMsg } from '../../../utilities/helpers'
import { LocalStorage } from '@/app/utilities/LocalStorage'
import { PaymentTypeIds } from '@getgreenline/homi-shared'
import { paymentNameFormatter } from '@getgreenline/shared'
import { cachePaymentOptions } from '@/app/utilities/paymentOptionsCache'

export type TerminalRelationshipsResponse = BlazepayModels.IIncluded<
  | BlazepayConfigModels.IConfigurationResponseAttribute<Record<string, unknown>>
  | BlazepayPaymentOptionModels.IPaymentOptionResponseContractAttribute
>[]

export class TerminalEventService extends AppSyncEventService implements IEventService {
  subscription?: Subscription
  private disposer?: IReactionDisposer
  private resourcesLoaded = 0

  @observable terminals =
    observable.map<BlazepayEnums.Provider, BlazepayTerminalModels.ITerminalEventOrRestData>()

  /**
   * @key config.attributes.provider: should be mapped with terminal.relationships
   * @value config
   */
  @observable configs =
    observable.map<BlazepayEnums.Provider, BlazepayConfigModels.IConfigurationEventOrRestData>()

  /**
   * @key paymentOption.attributes.provider: should be mapped with terminal.relationships
   * @value paymentOption
   */
  @observable paymentOptions =
    observable.map<
      BlazepayEnums.Provider,
      BlazepayPaymentOptionModels.IPaymentOptionEventOrRestData
    >()

  @observable ecomPaymentOptions =
    observable.map<
      BlazepayEnums.Provider,
      BlazepayPaymentOptionModels.IPaymentOptionEventOrRestData
    >()

  constructor(private merchantId: string, private terminalId: string) {
    super()
  }

  private appSyncCallback(): IAppSyncCallbacks<BlazepayTerminalModels.ITerminalSubscriptionResponseContract> {
    return {
      next: ({ data }) => {
        sentryWrapper.addBreadcrumb('terminal_event.appSyncCallback', { data })

        this.mapTerminals(this.terminals, data.updatedTerminal.data)

        const eventTerminalIncluded = data.updatedTerminal.included?.map((eventIncluded) => ({
          id: eventIncluded.id,
          type: eventIncluded.type,
          attributes: JSON.parse(eventIncluded.attributes),
        }))
        if (eventTerminalIncluded) {
          this.mapConfigsAndPaymentOptions(eventTerminalIncluded as TerminalRelationshipsResponse)
        }

        this.syncPaymentOptions()
      },
      error: (error) => {
        captureError(parseErrorMsg(error) || 'terminal_event.error', error, false)
      },
    }
  }

  subscribe<T>(props?: ISubscriptionCallbacks<T>) {
    this.reactToAppSyncConnectionChange()
    this.subscription = appsyncApi
      .subscribeFor(this.teardown)
      .terminalEvents(this.merchantId, this.terminalId, this.appSyncCallback())

    return props?.after()
  }

  unsubscribe() {
    this.subscription?.unsubscribe()
  }

  private teardown = () => {
    this.disposer?.()
  }

  private reactToAppSyncConnectionChange() {
    this.disposer = super.setDisposer(this.disposer, async (connectionState) => {
      await super.fetchRecentData(connectionState, this.keepDataUpToDate)
    })
  }

  @action
  private keepDataUpToDate = async () => {
    try {
      const terminalRes = await BlazepayAPI.getTerminals({
        merchantId: this.merchantId,
        filter: {
          deviceId: this.terminalId,
        },
        include: ['config', 'payment_option'],
      })

      this.incrementResourcesLoaded()

      sentryWrapper.addBreadcrumb('terminal_event.keepDataUpToDate', terminalRes, true)

      this.mapTerminals(this.terminals, terminalRes.data)
      if (terminalRes.included) {
        this.mapConfigsAndPaymentOptions(terminalRes.included)
        cachePaymentOptions({
          merchantId: this.merchantId,
          paymentOptions: terminalRes.included.filter(
            (included) => included.type === BlazepayEnums.RequestType.PAYMENT_OPTIONS,
          ) as BlazepayPaymentOptionModels.IPaymentOptionEventOrRestData[],
        })
      }
      this.syncPaymentOptions()
    } catch (error) {
      captureError(parseErrorMsg(error) || 'terminal_event.fetch.error', error, false)

      throw error
    }
  }

  private incrementResourcesLoaded = () => {
    if (this.resourcesLoaded >= 3) {
      return
    }

    this.resourcesLoaded += 1
  }

  private syncPaymentOptions = () => {
    const options = LocalStorage.getPaymentOptions()

    Object.keys(options).forEach((optionKey) => {
      const paymentTypeId = PaymentTypeIds[optionKey]
      const provider = BlazepayPaymentModels.getProvider(PaymentTypeIds[optionKey])
      if (!provider) return
      LocalStorage.setPaymentOption(paymentTypeId, this.isTurnedOn(provider))
    })
  }

  isTurnedOn(provider: BlazepayEnums.Provider) {
    const paymentOption = this.paymentOptions.get(provider)
    const config = this.configs.get(provider)
    const terminal = this.terminals.get(provider)

    sentryWrapper.addBreadcrumb(
      'terminal_event.isTurnedOn',
      {
        provider,
        paymentOption,
        config,
        terminal,
      },
      true,
    )

    if (!paymentOption || !config || !terminal) {
      if (this.resourcesLoaded >= 3) {
        sentryWrapper.captureMessage('terminal_event.isTurnedOn - missing data', {
          provider,
          paymentOption,
          config,
          terminal,
        })
      }

      return false
    }

    const isPosAvailable = paymentOption.attributes.posAvailable
    const isApproved = config.attributes.status === BlazepayConfigModels.ConfigStatus.APPROVED
    const posEnabled = !!config.attributes.posEnabled
    const isTerminalAttached = !terminal.attributes.isDeleted

    const isTurnedOn = isPosAvailable && isApproved && posEnabled && isTerminalAttached

    if (!isTurnedOn) {
      sentryWrapper.addBreadcrumb(
        'terminal_event.isTurnedOn - is turned off',
        {
          provider,
          paymentOption,
          config,
          terminal,
        },
        true,
      )
    }

    return isTurnedOn
  }

  @action
  private mapTerminals<T extends BlazepayTerminalModels.ITerminalEventOrRestData>(
    map: ObservableMap<BlazepayEnums.Provider, T>,
    data: T | T[],
  ) {
    const deserializedData = super.deserializeData<T>(data)

    if (Array.isArray(deserializedData)) {
      deserializedData.forEach((item) => this.mapSingleTerminal(map, item))
    } else {
      this.mapSingleTerminal(map, deserializedData)
    }
  }

  private mapSingleTerminal<T extends BlazepayTerminalModels.ITerminalEventOrRestData>(
    map: ObservableMap<BlazepayEnums.Provider, T>,
    item: T,
  ) {
    if (item.attributes.isDeleted === true) {
      map.delete(item.attributes.provider)
    } else {
      map.set(item.attributes.provider, item)
    }
  }

  @action
  private mapConfigsAndPaymentOptions(data: TerminalRelationshipsResponse) {
    const deserializedData = super.deserializeData<TerminalRelationshipsResponse>(data)

    deserializedData.forEach((item) => {
      if (item.type === BlazepayEnums.RequestType.PAYMENT_OPTIONS) {
        this.mapPaymentOption(
          item as unknown as BlazepayPaymentOptionModels.IPaymentOptionEventOrRestData,
        )
      } else if (item.type === BlazepayEnums.RequestType.PAYMENT_CONFIGS) {
        this.mapConfig(item as unknown as BlazepayConfigModels.IConfigurationEventOrRestData)
      }
    })
  }

  @action
  private mapPaymentOption<T extends BlazepayPaymentOptionModels.IPaymentOptionEventOrRestData>(
    item: T,
  ) {
    const { availableCountries, ecomAvailable, posAvailable, provider } = item.attributes

    if (availableCountries.includes(BlazepayEnums.CountryCode.CA)) {
      posAvailable ? this.paymentOptions.set(provider, item) : this.paymentOptions.delete(provider)
      ecomAvailable
        ? this.ecomPaymentOptions.set(provider, item)
        : this.ecomPaymentOptions.delete(provider)
    } else {
      this.paymentOptions.delete(provider)
      this.ecomPaymentOptions.delete(provider)
    }
  }

  @action
  private mapConfig<T extends BlazepayConfigModels.IConfigurationEventOrRestData>(item: T) {
    this.configs.set(item.attributes.provider, item)
  }

  formatPaymentOptionName(paymentTypeId: PaymentTypeIds) {
    const defaultPaymentName = paymentNameFormatter(paymentTypeId)
    const provider = BlazepayPaymentModels.getProvider(paymentTypeId)
    if (!provider) return defaultPaymentName

    const paymentOption = this.paymentOptions.get(provider)
    if (!paymentOption) return defaultPaymentName

    return paymentOption.attributes.posDisplayName || defaultPaymentName
  }

  getMinMaxChargeAmount({
    type,
    isRefund,
    paymentTypeId,
  }: {
    type: 'min' | 'max'
    isRefund: boolean
    paymentTypeId: PaymentTypeIds
  }): number | null {
    if (isRefund) return null

    const provider = BlazepayPaymentModels.getProvider(paymentTypeId)
    if (!provider) return null

    const paymentOption = this.paymentOptions.get(provider)
    if (!paymentOption) return null

    return type === 'min'
      ? paymentOption.attributes.minimumChargeAmount
      : paymentOption.attributes.maximumChargeAmount
  }

  getPaymentOptionIcon(paymentTypeId: PaymentTypeIds) {
    const provider = BlazepayPaymentModels.getProvider(paymentTypeId)
    if (!provider) return

    const paymentOption = this.paymentOptions.get(provider)
    return paymentOption?.attributes.iconUrl || undefined
  }

  @computed
  get ecomPaymentOptionsArray() {
    return Array.from(this.ecomPaymentOptions.values())
  }
}
